/**
 * Runtime Configuration
 *
 * Provides environment configuration that is injected at runtime
 * rather than baked in at build time. This enables a "build once, deploy
 * anywhere" model where a single Docker image is configured via environment
 * variables on the container.
 *
 * The server (server.ts) exposes a GET /config endpoint that returns
 * runtime env vars as JSON. The client fetches from this endpoint at
 * startup, before the app renders.
 */

export type AuthProviderType = "microsoft" | "google" | "dev";

export interface RuntimeConfig {
  /** Active authentication provider for this deployment. */
  authProvider: AuthProviderType;

  // Microsoft (Azure AD / Entra ID) — used when authProvider === "microsoft"
  azureClientId: string;
  azureTenantId: string;
  azureRedirectUri: string;
  apiScope: string;

  // Google Workspace — used when authProvider === "google"
  googleClientId: string;

  apiBaseUrl: string;
  streamApiUrl: string;
  /**
   * WebSocket base URL for long-lived connections (e.g. real-time
   * transcription). When empty, derived from streamApiUrl/apiBaseUrl by
   * swapping the http(s) scheme for ws(s). Lets WS traffic bypass the
   * frontend proxy the same way streamApiUrl does for SSE.
   *
   * Named `wsApiUrl` (not `wssApiUrl`) deliberately: it's scheme-agnostic —
   * secure `wss://` in deployed envs, plain `ws://` locally (e.g.
   * ws://localhost). The scheme is derived from the resolved endpoint.
   */
  wsApiUrl: string;
  appInsightsConnectionString: string;
  mode: string;
  isDev: boolean;

  /** Feature flags read from environment variables at runtime. */
  featureFlags: {
    brainstorm: boolean;
    autopilot: boolean;
    canvas: boolean;
    workflows: boolean;
    apps: boolean;
    microApps: boolean;
    multiBrand: boolean;
    agentRouting: boolean;
    agentHandoff: boolean;
    teamsIntegration: boolean;
    outlookIntegration: boolean;
    devMode: boolean;
    skillImport: boolean;
    rally: boolean;
  };
}
let _config: RuntimeConfig | null = null;

/**
 * Get the current runtime config. Throws if called before initialization.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!_config) {
    throw new Error("Runtime config not initialized. Call initRuntimeConfig() before accessing config.");
  }
  return _config;
}

/**
 * Check if the runtime config has been initialized.
 */
export function isRuntimeConfigReady(): boolean {
  return _config !== null;
}

/**
 * Set runtime config directly. Used in tests to bypass the /config fetch.
 */
export function setRuntimeConfig(config: RuntimeConfig): void {
  _config = config;
}

/**
 * Get the API base URL for making backend requests.
 *
 * This is provider-agnostic and reads from the runtime config.
 */
export function getApiBaseUrl(): string {
  return getRuntimeConfig().apiBaseUrl;
}

/**
 * Get the streaming API base URL for SSE connections.
 *
 * When configured, streaming endpoints connect directly to the backend
 * (bypassing the frontend proxy) to avoid holding proxy connections open
 * for long-lived SSE streams. Falls back to the regular API base URL if
 * not set.
 */
export function getStreamApiUrl(): string {
  const config = getRuntimeConfig();
  return config.streamApiUrl || config.apiBaseUrl;
}

/**
 * Get the WebSocket base URL for long-lived connections (e.g. real-time
 * transcription).
 *
 * Resolution order:
 *   1. Explicit `wsApiUrl` from runtime config (already ws(s)://).
 *   2. Derive from `streamApiUrl`/`apiBaseUrl` by swapping http(s)→ws(s).
 *      Relative URLs (e.g. "/api") are resolved against the page origin.
 */
export function getWsApiUrl(): string {
  const config = getRuntimeConfig();
  if (config.wsApiUrl) return config.wsApiUrl.replace(/\/$/, "");

  const httpBase = config.streamApiUrl || config.apiBaseUrl;
  // Resolve relative paths against the current origin so we can build an
  // absolute ws(s):// URL.
  const absolute = new URL(httpBase, globalThis.location?.origin ?? "http://localhost").toString();
  return absolute.replace(/^http/, "ws").replace(/\/$/, "");
}

/**
 * Initialize the runtime config by fetching from the /config endpoint.
 *
 * The server serves this endpoint, reading from runtime env vars
 * (set on the container, or from .env locally).
 */
export async function initRuntimeConfig(): Promise<RuntimeConfig> {
  if (_config) return _config;

  try {
    const response = await fetch("/config");
    if (!response.ok) {
      throw new Error(`Failed to fetch runtime config: ${response.status}`);
    }
    const data = await response.json();
    const mode = data.mode || "production";
    _config = {
      authProvider: (data.authProvider as AuthProviderType) || "microsoft",
      azureClientId: data.azureClientId || "",
      azureTenantId: data.azureTenantId || "",
      azureRedirectUri: data.azureRedirectUri || globalThis.location.origin,
      apiScope: data.apiScope || "",
      googleClientId: data.googleClientId || "",
      apiBaseUrl: data.apiBaseUrl || "/api",
      streamApiUrl: data.streamApiUrl || "",
      wsApiUrl: data.wsApiUrl || "",
      appInsightsConnectionString: data.appInsightsConnectionString || "",
      mode,
      isDev: mode === "development",
      featureFlags: {
        brainstorm: data.featureFlags?.brainstorm ?? false,
        autopilot: data.featureFlags?.autopilot ?? false,
        canvas: data.featureFlags?.canvas ?? false,
        workflows: data.featureFlags?.workflows ?? false,
        apps: data.featureFlags?.apps ?? false,
        microApps: data.featureFlags?.microApps ?? false,
        multiBrand: data.featureFlags?.multiBrand ?? false,
        agentRouting: data.featureFlags?.agentRouting ?? false,
        agentHandoff: data.featureFlags?.agentHandoff ?? false,
        teamsIntegration: data.featureFlags?.teamsIntegration ?? false,
        outlookIntegration: data.featureFlags?.outlookIntegration ?? false,
        devMode: data.featureFlags?.devMode ?? false,
        skillImport: data.featureFlags?.skillImport ?? false,
        rally: data.featureFlags?.rally ?? false,
      },
    };
  } catch (error) {
    console.error("Failed to load runtime config:", error);
    // Set a minimal fallback so getRuntimeConfig() doesn't throw, then
    // re-throw so the bootstrap in main.tsx can show a user-visible error.
    _config = {
      authProvider: "microsoft",
      azureClientId: "",
      azureTenantId: "",
      azureRedirectUri: globalThis.location.origin,
      apiScope: "",
      googleClientId: "",
      apiBaseUrl: "/api",
      streamApiUrl: "",
      wsApiUrl: "",
      appInsightsConnectionString: "",
      mode: "production",
      isDev: false,
      featureFlags: {
        brainstorm: false,
        autopilot: false,
        canvas: false,
        workflows: false,
        apps: false,
        microApps: false,
        multiBrand: false,
        agentRouting: false,
        agentHandoff: false,
        teamsIntegration: false,
        outlookIntegration: false,
        devMode: false,
        skillImport: false,
        rally: false,
      },
    };
    throw error;
  }

  return _config;
}
